// React and related imports
import React, { useEffect, useState } from 'react';

// Flowbite components
import { Dropdown, Checkbox, Button, Tooltip } from 'flowbite-react';

// Icons
import { HiX } from 'react-icons/hi';

// Interfaces and Enums
import { IQuestion } from '../../common/interfaces/Question.interface';
import { ContentState } from '../../hooks/useContentState';
import { ContentContextTypes } from '../../common/enums/ContentContextTypes';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// i18n hooks
import { useTranslation } from 'react-i18next';

interface QuestionsDropdownProps {
  questionsResponse: IQuestion[];
  contentState: ContentState;
  updateHandler: (field: keyof ContentState, value: IQuestion[]) => void;
  validateField: (field: string, value: string) => void;
}

export const QuestionsDropdown: React.FC<QuestionsDropdownProps> = ({
  questionsResponse,
  contentState,
  updateHandler,
  validateField,
}) => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);

  const [selectedQuestions, setSelectedQuestions] = useState<IQuestion[]>(
    contentState
      ? (Array.isArray(contentState.questions)
          ? contentState.questions
          : [contentState?.questions]
        ).filter((question): question is IQuestion => question !== undefined)
      : []
  );

  const validateQuestions = (updatedQuestions: IQuestion[]) => {
    if (contentState?.contextType === ContentContextTypes.CONTENT_ENGAGEMENT) {
      validateField(
        'contentEngagementQuestions',
        updatedQuestions.length ? 'valid' : ''
      );
    }
  };

  const handleQuestionSelect = (question: IQuestion) => {
    const updatedQuestions = selectedQuestions.some((q) => q.id === question.id)
      ? selectedQuestions.filter((q) => q.id !== question.id)
      : [...selectedQuestions, question];

    setSelectedQuestions(updatedQuestions);
    updateHandler('questions' as keyof ContentState, updatedQuestions);
    validateQuestions(updatedQuestions);
  };

  const removeQuestion = (questionId: string | undefined) => {
    if (!questionId) return;

    const updatedQuestions = selectedQuestions.filter(
      (q) => q.id !== questionId
    );

    setSelectedQuestions(updatedQuestions);
    updateHandler('questions' as keyof ContentState, updatedQuestions);
    validateQuestions(updatedQuestions);
  };

  useEffect(() => {
    validateQuestions(selectedQuestions);
  }, [contentState?.questions]);

  return (
    <div className="space-y-4">
      <Dropdown
        dismissOnClick={false}
        inline
        arrowIcon={false}
        label={
          <Button
            size="sm"
            className={`btn flex w-40 items-center ${
              isDarkMode ? 'btn-dark' : 'btn-light'
            }`}
            as="span"
          >
            {t('content.chooseQuestions')}
          </Button>
        }
        style={{
          maxHeight: '40vh',
          overflowY: 'auto',
        }}
      >
        <div className="max-h-[50vh] overflow-y-auto">
          {questionsResponse.map((question) => (
            <Tooltip
              key={question.id}
              className="w-fit-content"
              content={
                <div className="min-w-48 p-2">
                  <h6 className="mb-2 font-semibold">
                    {t('content.answers')}:
                  </h6>
                  <div className="space-y-1">
                    {question.answers?.length ? (
                      question.answers?.map((answer, index) => (
                        <ol key={index} className="text-sm">
                          {answer.dataSources?.[0]?.options.map((option, i) => (
                            <li
                              key={option.value[0].value}
                              className={
                                option.isCorrect ? 'text-green-400' : ''
                              }
                            >
                              {i + 1}. {option.value[0].value}
                            </li>
                          ))}
                        </ol>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400">
                        {t('content.noAnswers')}
                      </span>
                    )}
                  </div>
                </div>
              }
              placement="right"
              trigger="hover"
            >
              <Dropdown.Item
                className="flex items-center space-x-3 pr-8"
                onClick={() => handleQuestionSelect(question)}
              >
                <Checkbox
                  className="me-4"
                  checked={selectedQuestions.some((q) => q.id === question.id)}
                  onChange={() => handleQuestionSelect(question)}
                />
                <span className="flex-1 truncate">
                  {question.body?.[0]?.value}
                </span>
              </Dropdown.Item>
            </Tooltip>
          ))}
        </div>
      </Dropdown>

      {selectedQuestions.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedQuestions.map((question) => (
            <div
              key={question.id}
              className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 dark:bg-gray-300"
            >
              <span className="max-w-[200px] truncate text-sm">
                {question?.body?.[0]?.value}
              </span>
              <button
                onClick={() => removeQuestion(question.id)}
                className="text-gray-500 hover:text-gray-700"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-2 w-44 rounded-md bg-gray-100 px-3 py-1 text-center dark:bg-gray-300">
          <span className="text-sm">{t('content.noQuestionsSelected')}</span>
        </div>
      )}
    </div>
  );
};
